import * as Sentry from "@sentry/react";
import { useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useDeleteContact } from "@/hooks/useContacts.tsx";
import type { Contact } from "@/types/contact.ts";

/**
 * Confirms and performs a contact deletion. Copy matches the native app's
 * confirmation so both clients describe the same irreversible action the same way.
 *
 * `onDeleted` lets each host leave the contact it was showing — the sidebar
 * closes, a drill-down pops back to its list.
 */
export const ContactDeleteDialog = ({
  contact,
  open,
  onOpenChange,
  onDeleted,
}: {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) => {
  const deleteMutation = useDeleteContact(contact.id);
  const { mutateAsync, isPending } = deleteMutation;
  const contactId = contact.id;
  const name = contact.name || "this contact";

  const handleDelete = useCallback(async () => {
    try {
      await mutateAsync();
    } catch (error) {
      // An already-deleted contact resolves in the hook, so anything here is a real failure.
      Sentry.captureException(error, {
        tags: { component: "ContactDeleteDialog", operation: "deleteContact" },
        extra: { contactId },
      });
      errorToast("Failed to delete contact");
      // Leave the user where they are rather than acting on a failure.
      return;
    }
    onOpenChange(false);
    onDeleted();
  }, [mutateAsync, contactId, onOpenChange, onDeleted]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        // Escape bypasses the buttons, so without this it dismisses the dialog
        // mid-delete while Cancel is sitting there disabled.
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Delete contact</AlertDialogTitle>
          <AlertDialogDescription>
            Permanently delete {name}? This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} data-statsig-name="contact-delete-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Radix closes the dialog on action-select. Deleting is async and
              // can fail, so the close waits for `handleDelete`'s success path.
              e.preventDefault();
              void handleDelete();
            }}
            disabled={isPending}
            variant="destructive"
            data-statsig-name="contact-delete-confirm"
          >
            {isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
